import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, type DataTableColumn, type SortState } from '@/components/shared/DataTable';
import { formatRate } from '@/lib/metrics';
import { currentLocale, formatNumber } from '@/i18n/locale';
import type { CallerNumberStat } from '@/api/endpoints/analytics';
import { errorCode } from './LoadError';

/**
 * « Numéros » (W4, audit B2-analytics, Minari « Phone numbers »): the answer
 * rate of each caller number, so a line that stopped being picked up
 * (flagged as spam by the carriers) shows. Outbound calls only, counted by
 * the server (POST /api/analytics/dialer/numbers).
 */
export default function CallerNumbersTable({ rows, state, onRetry, error }: { rows: CallerNumberStat[]; state: 'loading' | 'error' | 'ready'; onRetry: () => void; error?: unknown }) {
    const { t } = useTranslation(['dialer', 'common']);
    const [sort, setSort] = useState<SortState | null>({ id: 'total', dir: 'desc' });
    const locale = currentLocale();

    const sorted = useMemo(() => {
        if (!sort) return rows;
        const key = sort.id as keyof CallerNumberStat;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[key] ?? -1;
            const y = b[key] ?? -1;
            return (typeof x === 'string' ? x.localeCompare(String(y)) : Number(x) - Number(y)) * dir;
        });
    }, [rows, sort]);

    const columns: DataTableColumn<CallerNumberStat>[] = [
        { id: 'number', header: t('dialer:analytics.numbers.number'), cell: (r) => <span className="font-vf-mono">{r.number || t('dialer:analytics.numbers.unknown')}</span>, sortable: true },
        { id: 'total', header: t('dialer:analytics.numbers.calls'), cell: (r) => formatNumber(r.total), sortable: true, align: 'right' },
        { id: 'answered', header: t('dialer:analytics.numbers.answered'), cell: (r) => formatNumber(r.answered), sortable: true, align: 'right', hideable: true },
        { id: 'answerRate', header: t('dialer:analytics.numbers.answerRate'), cell: (r) => formatRate(r.answerRate, locale), sortable: true, align: 'right' },
        { id: 'connected', header: t('dialer:analytics.numbers.connected'), cell: (r) => formatNumber(r.connected), sortable: true, align: 'right', hideable: true },
        { id: 'connectRate', header: t('dialer:analytics.numbers.connectRate'), cell: (r) => formatRate(r.connectRate, locale), sortable: true, align: 'right' },
        { id: 'meetings', header: t('dialer:analytics.numbers.meetings'), cell: (r) => formatNumber(r.meetings), sortable: true, align: 'right', hideable: true },
        {
            id: 'lastCallAt', header: t('dialer:analytics.numbers.lastCall'), sortable: true, align: 'right', hideable: true,
            cell: (r) => (r.lastCallAt ? new Date(r.lastCallAt).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'),
        },
    ];

    return (
        <section className="space-y-3" aria-labelledby="analytics-numbers-title">
            <div>
                <h2 id="analytics-numbers-title" className="text-sm font-semibold text-foreground">{t('dialer:analytics.numbers.title')}</h2>
                <p className="text-xs text-muted-foreground">{t('dialer:analytics.numbers.subtitle')}</p>
            </div>
            <DataTable
                caption={t('dialer:analytics.numbers.title')}
                columns={columns}
                rows={sorted}
                getRowId={(r) => r.number || '__none'}
                status={state}
                error={{ message: t('dialer:analytics.loadError'), code: errorCode(error), onRetry }}
                sort={sort}
                onSortChange={setSort}
                empty={{ title: t('dialer:analytics.noCallsPeriod') }}
                storageKey="analytics-numbers"
            />
        </section>
    );
}
